import { RunConfig, RunConfigCase } from '@aero-platform/shared/generated/AeroStructures'

export type DraftRole = 'Lead' | 'Trail'

export type DraftingCaseInfo = {
  gmId: string
  role: DraftRole
  partnerGmId: string
  offsetXm: number | null
  offsetYm: number | null
}

export const DRAFT_ROLE_FIELD = 'draft_role'
export const DRAFT_PAIR_INDEX_FIELD = 'draft_pair_index'

/**
 * Badge palette. Colour identifies the pair, the label identifies the role, so a run holding
 * several pairs stays readable. Index cycles once a run exceeds the palette length.
 */
export const DRAFT_PAIR_BADGE_CLASSES = [
  'bg-emerald-600 text-white',
  'bg-blue-600 text-white',
  'bg-amber-600 text-white',
  'bg-violet-600 text-white',
  'bg-rose-600 text-white',
  'bg-teal-600 text-white'
]

export const getDraftPairBadgeClass = (pairIndex: number) =>
  DRAFT_PAIR_BADGE_CLASSES[
    (((pairIndex - 1) % DRAFT_PAIR_BADGE_CLASSES.length) + DRAFT_PAIR_BADGE_CLASSES.length) %
      DRAFT_PAIR_BADGE_CLASSES.length
  ]

/**
 * Tag headline rows that take part in a drafting pair with their role and a per-run pair number.
 * Rows with no drafting info are returned by reference and gain no new keys, so every downstream
 * consumer of non-drafting rows behaves exactly as before.
 */
export const annotateDraftingPairs = (rows: any[], draftingInfo: DraftingCaseInfo[]): any[] => {
  if (!rows?.length || !draftingInfo?.length) return rows

  const infoByGmId = new Map<string, DraftingCaseInfo>()
  draftingInfo.forEach(info => {
    if (info?.gmId) infoByGmId.set(String(info.gmId), info)
  })
  if (!infoByGmId.size) return rows

  const pairIndexByKey = new Map<string, number>()
  const nextPairIndexByRun = new Map<string, number>()

  return rows.map(row => {
    const info = infoByGmId.get(String(row?.gm_id))
    if (!info) return row

    const gmId = String(row.gm_id)
    // Orphan (partner outside the loaded set) keys on itself so it still gets its own colour.
    const pairMembers = info.partnerGmId ? [gmId, String(info.partnerGmId)].sort() : [gmId]
    const runKey = `${row.project}::${row.run}`
    const pairKey = `${runKey}::${pairMembers.join('-')}`

    let pairIndex = pairIndexByKey.get(pairKey)
    if (pairIndex === undefined) {
      pairIndex = nextPairIndexByRun.get(runKey) ?? 1
      pairIndexByKey.set(pairKey, pairIndex)
      nextPairIndexByRun.set(runKey, pairIndex + 1)
    }

    return {
      ...row,
      [DRAFT_ROLE_FIELD]: info.role,
      [DRAFT_PAIR_INDEX_FIELD]: pairIndex
    }
  })
}

// Workflow template the backend assigns to a drafting run's trail car case.
const FOLLOWING_CAR_TEMPLATE_NAME = 'FOLLOWING_CAR'

export const isDraftingRunConfig = (
  runConfig?: Pick<RunConfig, 'followingCars' | 'complianceDesc'>
): boolean =>
  (runConfig?.followingCars?.length ?? 0) > 0 || runConfig?.complianceDesc === 'Drafting'

/**
 * Cases to seed a duplicated run with. A drafting run's stored selectedCases also hold each trail
 * car's case, which the backend appends (flagged followingCarCase) once its case file lands and
 * which shares the lead's attitude state. Copying those collides with the lead's case name, and the
 * new run re-creates its trail cases from runConfig.followingCars anyway. Trail cases stored before
 * the flag existed are caught by keeping only the first case per attitude state, which is limited
 * to drafting runs so other runs keep every case. Per-position following-car data is dropped so the
 * backend expands every following car afresh.
 */
export const getDuplicableCases = (
  cases: RunConfigCase[] | undefined,
  isDraftingRun: boolean
): RunConfigCase[] => {
  if (!cases?.length) return []

  const seenStateNames = new Set<string>()
  return cases.reduce<RunConfigCase[]>((result, caseItem) => {
    if (!caseItem || caseItem.followingCarCase) return result

    if (isDraftingRun) {
      const stateName = caseItem.selectedAttitudeState?.name
      if (stateName) {
        if (seenStateNames.has(stateName)) return result
        seenStateNames.add(stateName)
      }
    }

    const isTrailTemplate = caseItem.templateOverride?.name === FOLLOWING_CAR_TEMPLATE_NAME
    if (!caseItem.selectedFollowingCar && !isTrailTemplate) {
      result.push(caseItem)
      return result
    }

    const { selectedFollowingCar, templateOverride, ...rest } = caseItem as any
    result.push({
      ...rest,
      ...(templateOverride && !isTrailTemplate ? { templateOverride } : {})
    } as RunConfigCase)
    return result
  }, [])
}
